// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #139 Phase 8.3 — Next Day Openings Dashboard: build the
// region → location-codes map from a set of office documents.
//
// Each office doc has (will have, post-bulk-push) a `region` field — a
// free-text label like "Southeast" / "Northeast" / "West" / etc.
// Multiple offices share the same region; this helper groups them.
//
// Pure — takes the raw set of office docs (keyed by location code so the
// caller can supply partial fetches) and returns a sorted region map
// suitable for rendering region pills + region toggles.
//
// Used by the dashboard's region filter pills. Region SELECTION is not
// persisted as its own field — clicking a region is a shortcut that
// directly mutates `Users.dashboardLocations`. The pill's visual state
// (all/some/none selected) is derived from the current dashboardLocations
// vs the region's locations.

/**
 * @param {Record<string, OfficeDoc|null>} officeDocsByLocation
 *   Map from location code → office doc (or null when fetch incomplete).
 *   The office doc shape is whatever `getOfficeMetaDataByLocation`
 *   returns from the Lambda. The only field we care about is `region`.
 * @returns {Array<{ region: string, locations: string[] }>}
 *   Sorted array of `{ region, locations }` tuples — region names sorted
 *   by their leading numeric prefix when present (so "3 - East" sorts
 *   before "12"), then alphabetically. Locations within each region are
 *   alphabetically sorted.
 *
 *   Why an array rather than a plain `{ region: locations }` object:
 *   JavaScript objects iterate integer-like keys numerically BEFORE
 *   string keys regardless of insertion order, which would break our
 *   ordered render (e.g. "12" would appear before "3 - East" because
 *   "12" is an integer-keyed entry). An array of tuples preserves the
 *   exact sort order we want.
 *
 *   Offices without a region field are SKIPPED — they don't appear in
 *   any region grouping but remain manually selectable as individual
 *   locations.
 */
export function buildRegionLocationMap(officeDocsByLocation) {
  const grouping = new Map(); // insertion-order-preserving (any key)
  if (!officeDocsByLocation || typeof officeDocsByLocation !== "object") {
    return [];
  }

  for (const [locationCode, doc] of Object.entries(officeDocsByLocation)) {
    if (!locationCode) continue;
    if (!doc) continue;
    // Field name `Region` (capital R) matches the Offices collection's
    // PascalCase convention (sibling to `Active`, `Location`, `OfficeID`,
    // `ScheduleInfo`). Verified via dbtools/src/queries/offices-region-check.mjs
    // against UAT data on 2026-05-18.
    const rawRegion = doc.Region;
    if (typeof rawRegion !== "string") continue;
    const region = rawRegion.trim();
    if (region === "") continue;
    if (!grouping.has(region)) grouping.set(region, []);
    const list = grouping.get(region);
    if (!list.includes(locationCode)) list.push(locationCode);
  }

  // Build the result array, sorted by region with the leading-numeric
  // comparator + locations sorted alphabetically within each.
  const tuples = Array.from(grouping.entries()).map(([region, locations]) => ({
    region,
    locations: locations.slice().sort(),
  }));
  tuples.sort((a, b) => naturalRegionCompare(a.region, b.region));
  return tuples;
}

// Exported for tests + potential reuse. Pure.
//
//   numericA + numericB → compare numeric values; tiebreak on suffix
//   numericA only       → numericA first (digits before pure text)
//   numericB only       → numericB first
//   neither             → alphabetical (case-aware)
export function naturalRegionCompare(a, b) {
  const matchA = /^(\d+)(.*)$/.exec(String(a));
  const matchB = /^(\d+)(.*)$/.exec(String(b));
  if (matchA && matchB) {
    const numA = parseInt(matchA[1], 10);
    const numB = parseInt(matchB[1], 10);
    if (numA !== numB) return numA - numB;
    return matchA[2].localeCompare(matchB[2], undefined, {
      caseFirst: "upper",
    });
  }
  if (matchA) return -1;
  if (matchB) return 1;
  return String(a).localeCompare(String(b), undefined, { caseFirst: "upper" });
}
